/**
 * Off-screen render target for the "Export fac-build-msup org" feature.
 *
 * Composes, side by side:
 *   [ Bounded SVG map (active hexagons, region labels, facility/build/msup markers) ]
 *   [ Static panel listing Facilities (merged), Build bases, MSup (merged) ]
 *
 * Designed to be rasterised by `html-to-image`. No interactions, no animations.
 */

import type { CSSProperties } from 'react';
import type { MapArrow, MapObject, WarPlanDTO } from '@warplan/shared';
import {
  ATLAS,
  HEX_VERTICES,
  REGION_LABELS,
  TILE_W,
  TILE_H,
  findRegionLabel,
  tileAssetFor,
  type MapBackgroundKind,
} from '../lib/foxhole-maps.js';
import { getIconEntry } from '../lib/icon-catalog.js';
import { ExportArrows } from './ExportArrows.js';
import {
  EXPORT_MAP_MAX_HEIGHT_PX,
  EXPORT_MAP_WIDTH_PX,
  EXPORT_PANEL_WIDTH_PX,
  type ExportBoundingBox,
} from '../lib/war-plan-export.js';
import {
  resolveIconCollisions,
} from '../lib/resolve-icon-collisions.js';
import {
  ASSMAT_SHORT,
  MSUP_VARIANT_SHORT,
  buildFacilityItems,
  buildMsupItems,
  selectBuilds,
  type FacBuildMsupScope,
  type FacilityItem,
  type MsupItem,
} from '../lib/fac-build-msup-export.js';
import type { ExportWarLabel } from './WarPlanExportView.js';
import { WarPlanStrategyBlock } from './WarPlanExportView.js';

export interface FacBuildMsupExportViewProps {
  bbox: ExportBoundingBox;
  /** Markers already filtered to active regions + fac/build/msup scope. */
  markers: ReadonlyArray<MapObject>;
  /**
   * Optional broader set of map objects used only to compute de-overlap
   * positions, ensuring the export arrangement matches the interactive view
   * (which sees all objects, not just the export subset).
   */
  collisionContext?: ReadonlyArray<MapObject>;
  /** Pre-filtered arrows to render on top of the map. */
  arrows: ReadonlyArray<MapArrow>;
  disabledRegionKeys: ReadonlySet<string>;
  mapBackground: MapBackgroundKind;
  warLabels: ReadonlyArray<ExportWarLabel>;
  /** Optional war plan to render the strategy block at the top of the panel. */
  warPlan: WarPlanDTO | null;
  /** Which sections to render in the right column. */
  scope?: FacBuildMsupScope;
  /** Optional caption (e.g. generation timestamp). */
  caption?: string;
  /** Optional title displayed at the top of the right column. */
  title?: string;
}

const REGION_LABEL_FONT_SIZE = 38;

/** Attribution shown on exported images when the 'ink' background mod is active. */
const INK_CREDIT = 'Fond de carte « Ink & Frontline » par Arkhitecton';
/**
 * Rendered size of a marker icon in the export (SVG/world units).
 * Chosen as ~1.75× the interactive base size (32) to keep markers clearly
 * visible without overwhelming dense clusters.
 */
const MARKER_ICON_SIZE = 56;
const LIST_ICON_SIZE = 22;
/**
 * Inverse-scale factor used for the export's de-overlap computation.
 * Mirrors `1 / MIN_ICON_SCALE` from the interactive view (MapPage.tsx) so the
 * export sees the same collision zones the user sees at maximum interactive
 * zoom — keeping the icon arrangement identical between the UI and the PNG.
 */
const hexPoints = HEX_VERTICES.map((p) => `${p.x},${p.y}`).join(' ');

function findNearestLabel(
  wx: number,
  wy: number,
  warLabels: ReadonlyArray<ExportWarLabel>,
  kind: 'major' | 'minor',
): string | null {
  let best: ExportWarLabel | null = null;
  let bestDist = Infinity;
  for (const lbl of warLabels) {
    if (lbl.kind !== kind) continue;
    const dx = lbl.wx - wx;
    const dy = lbl.wy - wy;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = lbl;
    }
  }
  return best?.text ?? null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FacBuildMsupExportView({
  bbox,
  markers,
  collisionContext,
  arrows,
  disabledRegionKeys,
  mapBackground,
  warLabels,
  warPlan,
  scope = 'all',
  caption,
  title,
}: FacBuildMsupExportViewProps): React.JSX.Element {
  const aspect = bbox.width / bbox.height;
  let mapWidth = EXPORT_MAP_WIDTH_PX;
  let mapHeight = mapWidth / aspect;
  if (mapHeight > EXPORT_MAP_MAX_HEIGHT_PX) {
    mapHeight = EXPORT_MAP_MAX_HEIGHT_PX;
    mapWidth = mapHeight * aspect;
  }

  const facilityItems = scope === 'msup' ? [] : buildFacilityItems(markers);
  const msupItems = scope === 'fac-build' ? [] : buildMsupItems(markers);
  const builds = scope === 'msup' ? [] : selectBuilds(markers);

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
    padding: 16,
    background: '#0f172a',
    color: '#f1f5f9',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    width: mapWidth + EXPORT_PANEL_WIDTH_PX + 32 + 16,
  };

  const mapColumnStyle: CSSProperties = {
    position: 'relative',
    width: mapWidth,
    height: mapHeight,
    background: '#020617',
    borderRadius: 6,
    overflow: 'hidden',
  };

  const captionStyle: CSSProperties = {
    position: 'absolute',
    bottom: 6,
    right: 8,
    fontSize: 12,
    color: '#cbd5e1',
    textShadow: '0 0 4px rgba(0,0,0,0.9)',
  };

  const inkCreditStyle: CSSProperties = {
    position: 'absolute',
    bottom: 6,
    left: 8,
    fontSize: 10,
    color: '#94a3b8',
    textShadow: '0 0 4px rgba(0,0,0,0.9)',
  };

  return (
    <div style={containerStyle}>
      <div style={mapColumnStyle}>
        <svg
          width={mapWidth}
          height={mapHeight}
          viewBox={`${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', background: '#020617' }}
        >
          <g>
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <image
                  key={tile.key}
                  href={tileAssetFor(tile, mapBackground)}
                  x={tile.x - TILE_W / 2}
                  y={tile.y - TILE_H / 2}
                  width={TILE_W}
                  height={TILE_H}
                  preserveAspectRatio="none"
                />
              );
            })}
          </g>
          <g pointerEvents="none">
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <polygon
                  key={`outline-${tile.key}`}
                  points={hexPoints}
                  transform={`translate(${tile.x} ${tile.y})`}
                  fill="none"
                  stroke="#94a3b8"
                  strokeOpacity={0.45}
                  strokeWidth={2}
                />
              );
            })}
          </g>
          <g pointerEvents="none">
            {(() => {
              // De-overlap is computed over the full collision context so the
              // export arrangement matches what the user sees on the map.
              const contextSource = collisionContext ?? markers;
              const sortedContext = [...contextSource].sort(
                (a, b) =>
                  new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
              );
              const rects = sortedContext.map((m) => ({
                id: m.id,
                x: m.position.x,
                y: m.position.y,
                scale: m.metadata.scale ?? 1,
                // Use the actual rendered size so AABB matches visual bounds.
                size: MARKER_ICON_SIZE * (m.metadata.scale ?? 1),
              }));
              const adjusted = resolveIconCollisions(rects);
              const renderOrder = [...markers].sort(
                (a, b) =>
                  new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
              );
              return renderOrder.map((m) => {
                const entry = getIconEntry(m.kind, m.metadata.iconId);
                const size = MARKER_ICON_SIZE * (m.metadata.scale ?? 1);
                const pos = adjusted.get(m.id) ?? m.position;
                return (
                  <image
                    key={m.id}
                    href={entry.src}
                    x={pos.x - size / 2}
                    y={pos.y - size / 2}
                    width={size}
                    height={size}
                    preserveAspectRatio="xMidYMid meet"
                  />
                );
              });
            })()}
          </g>
          <ExportArrows arrows={arrows} />
          <g pointerEvents="none">
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <text
                  key={`label-${tile.key}`}
                  x={tile.x}
                  y={tile.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={REGION_LABEL_FONT_SIZE}
                  fontFamily="sans-serif"
                  fontWeight={700}
                  fill="#ffffff"
                  stroke="#0f172a"
                  strokeWidth={4}
                  style={{ paintOrder: 'stroke fill' }}
                >
                  {REGION_LABELS[tile.key] ?? tile.key}
                </text>
              );
            })}
          </g>
        </svg>

        {mapBackground === 'ink' ? <div style={inkCreditStyle}>{INK_CREDIT}</div> : null}
        {caption ? <div style={captionStyle}>{caption}</div> : null}
      </div>

      <FacBuildMsupPanel
        title={title}
        warPlan={warPlan}
        facilityItems={facilityItems}
        builds={builds}
        msupItems={msupItems}
        warLabels={warLabels}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right column
// ---------------------------------------------------------------------------

interface PanelProps {
  title?: string | undefined;
  warPlan: WarPlanDTO | null;
  facilityItems: ReadonlyArray<FacilityItem>;
  builds: ReadonlyArray<MapObject>;
  msupItems: ReadonlyArray<MsupItem>;
  warLabels: ReadonlyArray<ExportWarLabel>;
}

function FacBuildMsupPanel({
  title,
  warPlan,
  facilityItems,
  builds,
  msupItems,
  warLabels,
}: PanelProps): React.JSX.Element {
  const wrapper: CSSProperties = {
    width: EXPORT_PANEL_WIDTH_PX,
    background: 'rgba(15, 23, 42, 0.95)',
    border: '1px solid #334155',
    borderRadius: 6,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };
  const titleStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    paddingBottom: 8,
    borderBottom: '1px solid #334155',
    color: '#f1f5f9',
  };

  return (
    <aside style={wrapper}>
      {title ? <div style={titleStyle}>{title}</div> : null}

      <WarPlanStrategyBlock warPlan={warPlan} titleStyle={titleStyle} />

      {facilityItems.length > 0 && (
        <ListSection
          fr="Facilities"
          en="Facilities"
          count={facilityItems.length}
          first={false}
        >
          {facilityItems.map((item, idx) => (
            <FacilityRow key={`fac-${idx}`} item={item} warLabels={warLabels} />
          ))}
        </ListSection>
      )}

      {builds.length > 0 && (
        <ListSection
          fr="Build"
          en="Build bases"
          count={builds.length}
          first={false}
        >
          {builds.map((obj) => (
            <PlainRow key={obj.id} obj={obj} warLabels={warLabels} />
          ))}
        </ListSection>
      )}

      {msupItems.length > 0 && (
        <ListSection
          fr="MSup"
          en="Maintenance"
          count={msupItems.length}
          first={false}
        >
          {msupItems.map((item, idx) => (
            <MsupRow key={`msup-${idx}`} item={item} warLabels={warLabels} />
          ))}
        </ListSection>
      )}

      {facilityItems.length === 0 && builds.length === 0 && msupItems.length === 0 && (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>Aucun asset à exporter.</p>
      )}
    </aside>
  );
}

interface ListSectionProps {
  fr: string;
  en: string;
  count: number;
  first: boolean;
  children: React.ReactNode;
}

function ListSection({ fr, en, count, first, children }: ListSectionProps): React.JSX.Element {
  const headerStyle: CSSProperties = {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#cbd5e1',
    lineHeight: 1.15,
    paddingTop: first ? 0 : 6,
    borderTop: first ? 'none' : '1px solid #334155',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={headerStyle}>
        <div>
          {fr} <span style={{ color: '#94a3b8' }}>({count})</span>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 11 }}>{en}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row variants
// ---------------------------------------------------------------------------

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '4px 6px',
  background: 'rgba(30, 41, 59, 0.6)',
  borderRadius: 4,
};

function Location({
  wx,
  wy,
  warLabels,
}: {
  wx: number;
  wy: number;
  warLabels: ReadonlyArray<ExportWarLabel>;
}): React.JSX.Element {
  const region = findRegionLabel(wx, wy);
  const major = findNearestLabel(wx, wy, warLabels, 'major');
  const minor = findNearestLabel(wx, wy, warLabels, 'minor');
  return (
    <>
      <div style={{ color: '#cbd5e1', fontSize: 11, lineHeight: 1.25 }}>
        {region}
        {major ? ` / ${major}` : ''}
      </div>
      {minor && (
        <div style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.25 }}>
          {minor}
        </div>
      )}
    </>
  );
}

function PlainRow({
  obj,
  warLabels,
}: {
  obj: MapObject;
  warLabels: ReadonlyArray<ExportWarLabel>;
}): React.JSX.Element {
  const entry = getIconEntry(obj.kind, obj.metadata.iconId);
  return (
    <div style={rowStyle}>
      <img
        src={entry.src}
        alt=""
        width={LIST_ICON_SIZE}
        height={LIST_ICON_SIZE}
        style={{ objectFit: 'contain', flexShrink: 0, marginTop: 1 }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
          {obj.metadata.note?.trim() || entry.label}
        </div>
        <Location wx={obj.position.x} wy={obj.position.y} warLabels={warLabels} />
      </div>
    </div>
  );
}

function FacilityRow({
  item,
  warLabels,
}: {
  item: FacilityItem;
  warLabels: ReadonlyArray<ExportWarLabel>;
}): React.JSX.Element {
  if (item.kind === 'standalone') {
    return <PlainRow obj={item.obj} warLabels={warLabels} />;
  }
  const { material, assmats } = item;
  const entry = getIconEntry(material.kind, material.metadata.iconId);
  const amParts = assmats.map((a) => ASSMAT_SHORT[a.metadata.iconId] ?? a.metadata.iconId);
  const amLabel =
    amParts.length === 0
      ? ''
      : amParts[0] +
        (amParts.length > 1
          ? '+' + amParts.slice(1).map((s) => s.replace(/^AM/, '')).join('+')
          : '');
  const baseName = material.metadata.note?.trim() || 'Material';
  const label = amLabel ? `${baseName} (${amLabel})` : baseName;

  return (
    <div style={rowStyle}>
      <img
        src={entry.src}
        alt=""
        width={LIST_ICON_SIZE}
        height={LIST_ICON_SIZE}
        style={{ objectFit: 'contain', flexShrink: 0, marginTop: 1 }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
          {label}
        </div>
        <Location
          wx={material.position.x}
          wy={material.position.y}
          warLabels={warLabels}
        />
      </div>
    </div>
  );
}

function MsupRow({
  item,
  warLabels,
}: {
  item: MsupItem;
  warLabels: ReadonlyArray<ExportWarLabel>;
}): React.JSX.Element {
  if (item.kind === 'standalone') {
    return <PlainRow obj={item.obj} warLabels={warLabels} />;
  }
  const { tunnel, msup } = item;
  const entry = getIconEntry(tunnel.kind, tunnel.metadata.iconId);
  const short = MSUP_VARIANT_SHORT[msup.metadata.iconId] ?? msup.metadata.iconId;
  const baseName = tunnel.metadata.note?.trim() || 'Maintenance Tunnel';
  const label = `${baseName} (${short})`;
  return (
    <div style={rowStyle}>
      <img
        src={entry.src}
        alt=""
        width={LIST_ICON_SIZE}
        height={LIST_ICON_SIZE}
        style={{ objectFit: 'contain', flexShrink: 0, marginTop: 1 }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
          {label}
        </div>
        <Location wx={tunnel.position.x} wy={tunnel.position.y} warLabels={warLabels} />
      </div>
    </div>
  );
}
